import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { CustomSqlSnippet, SqlSnippetEngineId } from '../types/sqlSnippetTypes';
import { useSqlSnippetsStore } from '../store/sqlSnippetsStore';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SqlSnippetEditorDialog } from './SqlSnippetEditorDialog';
const ENGINE_LABELS: Record<SqlSnippetEngineId, string> = {
  mssql: 'SQL Server',
  pgsql: 'PostgreSQL',
  sqlite: 'SQLite',
  mysql: 'MySQL',
  oracle: 'Oracle',
};
export function SqlSnippetsSettingsSection() {
  const [engineId, setEngineId] = useState<SqlSnippetEngineId>('mssql');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<CustomSqlSnippet | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<CustomSqlSnippet | undefined>(undefined);
  const [pendingClearEngine, setPendingClearEngine] = useState<SqlSnippetEngineId | undefined>(undefined);

  const snippetsByEngine = useSqlSnippetsStore((state) => state.snippetsByEngine);
  const deleteSnippet = useSqlSnippetsStore((state) => state.deleteSnippet);
  const clearEngineSnippets = useSqlSnippetsStore((state) => state.clearEngineSnippets);

  const customSnippets = useMemo(() => snippetsByEngine[engineId] ?? [], [engineId, snippetsByEngine]);

  const openAdd = () => {
    setEditingSnippet(undefined);
    setEditorOpen(true);
  };

  const openEdit = (snippet: CustomSqlSnippet) => {
    setEditingSnippet(snippet);
    setEditorOpen(true);
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">SQL Snippets</div>

      <div className="rounded-md border border-border bg-background p-3 space-y-3">
        <div className="flex items-end gap-3">
          <div className="grid gap-1.5 flex-1">
            <Label>Engine</Label>
            <Select value={engineId} onValueChange={(value) => setEngineId(value as SqlSnippetEngineId)}>
              <SelectTrigger data-testid="sql-snippets-engine-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mssql">SQL Server</SelectItem>
                <SelectItem value="pgsql">PostgreSQL</SelectItem>
                <SelectItem value="sqlite">SQLite</SelectItem>
                <SelectItem value="mysql">MySQL</SelectItem>
                <SelectItem value="oracle">Oracle</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              Custom snippets show up in completions at the start of a statement.
            </div>
          </div>

          <Button type="button" size="sm" className="gap-2" onClick={openAdd} data-testid="add-sql-snippet">
            <Plus className="h-4 w-4" />
            Add snippet
          </Button>
        </div>

        <div className="rounded-md border border-border/50 overflow-hidden">
          {customSnippets.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">No custom snippets for {ENGINE_LABELS[engineId]}.</div>
          ) : (
            <div className="divide-y divide-border/30">
              {customSnippets.map((snippet) => (
                <div key={snippet.id} className="flex items-start gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {snippet.prefix}
                      </Badge>
                      <div className="text-xs font-medium truncate">{snippet.label}</div>
                    </div>
                    {snippet.description ? (
                      <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                        {snippet.description}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => openEdit(snippet)}
                      data-testid={`edit-sql-snippet-${snippet.id}`}
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => setPendingDelete(snippet)}
                      data-testid={`delete-sql-snippet-${snippet.id}`}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPendingClearEngine(engineId)}
            disabled={customSnippets.length === 0}
            data-testid="clear-sql-snippets-engine"
          >
            Clear {ENGINE_LABELS[engineId]} snippets
          </Button>
        </div>
      </div>

      <SqlSnippetEditorDialog
        key={editingSnippet?.id ?? `new-${engineId}`}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        snippet={editingSnippet}
        defaultEngineId={engineId}
      />

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(open) => !open && setPendingDelete(undefined)}>
        <AlertDialogContent data-testid="delete-sql-snippet-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete snippet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-mono">{pendingDelete?.prefix}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDelete(undefined)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              onClick={() => {
                if (pendingDelete) {
                  deleteSnippet(pendingDelete.id);
                }
                setPendingDelete(undefined);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(pendingClearEngine)} onOpenChange={(open) => !open && setPendingClearEngine(undefined)}>
        <AlertDialogContent data-testid="clear-sql-snippets-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear engine snippets?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes all custom snippets for {pendingClearEngine ? ENGINE_LABELS[pendingClearEngine] : 'this engine'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingClearEngine(undefined)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              onClick={() => {
                if (pendingClearEngine) {
                  clearEngineSnippets(pendingClearEngine);
                }
                setPendingClearEngine(undefined);
              }}
            >
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
